import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Home, Loader2, MapPin, Navigation, ShieldCheck } from "lucide-react";
import { errText, gpsConsent, gpsSend, gpsToday } from "@/lib/bf";

const EVERY_MS = 3 * 60 * 1000;
export const GPS_ON = false;
const hm = (iso) => (iso ? new Date(iso).toLocaleTimeString("ro-RO", { hour: "2-digit", minute: "2-digit", timeZone: "Europe/Bucharest" }) : "");
const locate = () => new Promise((ok, no) => {
  if (!navigator.geolocation) return no(new Error("Telefonul nu permite locația"));
  navigator.geolocation.getCurrentPosition((p) => ok({ lat: p.coords.latitude, lng: p.coords.longitude, acc: p.coords.accuracy }),
    () => no(new Error("Permite accesul la locație din browser ca să deschizi ziua")), { enableHighAccuracy: true, timeout: 20000, maximumAge: 60000 });
});

const useTracker = (on, setDay) => {
  const last = useRef(0);
  useEffect(() => {
    if (!on || !navigator.geolocation) return undefined;
    const send = (p) => {
      if (Date.now() - last.current < EVERY_MS) return;
      last.current = Date.now();
      gpsSend("point", { lat: p.coords.latitude, lng: p.coords.longitude, acc: p.coords.accuracy }).then((r) => {
        setDay(r.day);
        if (!r.open && r.day?.end_auto) toast.success("Ai ajuns acasă: ziua s-a închis automat. Mulțumim!");
      }).catch(() => {});
    };
    const id = navigator.geolocation.watchPosition(send, () => {}, { enableHighAccuracy: true, maximumAge: 60000, timeout: 30000 });
    return () => navigator.geolocation.clearWatch(id);
  }, [on, setDay]);
};

export default function BFDayGps() {
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState("");
  const setDay = useCallback((day) => setS((x) => ({ ...x, day })), []);
  useEffect(() => { gpsToday().then(setS).catch(() => setS(false)); }, []);
  const open = !!(s?.day && !s.day.end_at);
  useTracker(open, setDay);
  if (!s) return null;
  const d = s.day;
  const act = (kind) => {
    setBusy(kind);
    locate().then((p) => gpsSend(kind, p)).then((day) => { setDay(day); toast.success(kind === "open" ? (d ? "Ziua a fost redeschisă." : "Ziua a început. Spor la vizite!") : "Ziua s-a închis."); })
      .catch((e) => toast.error(e.message && !e.response ? e.message : errText(e, "Nu am putut salva locația"))).finally(() => setBusy(""));
  };
  const accept = () => { setBusy("consent"); gpsConsent().then(() => setS({ ...s, consent: true })).catch((e) => toast.error(errText(e, "Nu am putut salva acordul"))).finally(() => setBusy("")); };
  if (!s.consent) return (
    <div className="bf-gps is-consent" data-testid="bf-gps-consent">
      <ShieldCheck size={20} />
      <div><b>Acord pentru locație</b><p>Când apeși „Deschide ziua”, aplicația îți salvează locația cât timp e deschisă pe telefon, până închizi ziua sau ajungi înapoi de unde ai plecat. Locația se folosește doar pentru raportul de traseu (km, ore, vizite), vizibil doar adminului. Nu se urmărește nimic în afara zilei de lucru deschise.</p></div>
      <button type="button" className="st-btn st-btn--silver" onClick={accept} disabled={!!busy} data-testid="bf-gps-consent-accept">{busy ? <Loader2 size={14} className="animate-spin" /> : <ShieldCheck size={14} />} Sunt de acord</button>
    </div>
  );
  return (
    <div className={`bf-gps ${open ? "is-open" : d?.end_at ? "is-closed" : ""}`} data-testid="bf-gps">
      {open ? <Navigation size={20} /> : d?.end_at ? <Home size={20} /> : <MapPin size={20} />}
      <div>
        <b data-testid="bf-gps-status">{open ? `Ziua e deschisă din ${hm(d.start_at)}` : d?.end_at ? `Zi încheiată: ${hm(d.start_at)} – ${hm(d.end_at)}${d.end_auto ? " (ajuns acasă)" : ""}` : "Începe ziua de lucru"}</b>
        <p data-testid="bf-gps-stats">{d ? `${d.km} km parcurși${d.hours ? ` · ${d.hours}` : ""}` : "Apasă „Deschide ziua” când pleci. Ține aplicația deschisă pe telefon; ziua se închide singură când revii de unde ai plecat."}</p>
      </div>
      {!d && <button type="button" className="st-btn st-btn--silver" onClick={() => act("open")} disabled={!!busy} data-testid="bf-gps-open">{busy ? <Loader2 size={14} className="animate-spin" /> : <Navigation size={14} />} Deschide ziua</button>}
      {d?.end_at && <button type="button" className="st-btn st-btn--ghost" onClick={() => act("open")} disabled={!!busy} data-testid="bf-gps-reopen">{busy ? <Loader2 size={14} className="animate-spin" /> : <Navigation size={14} />} Redeschide ziua</button>}
      {open && <button type="button" className="st-btn st-btn--ghost" onClick={() => act("close")} disabled={!!busy} data-testid="bf-gps-close">{busy ? <Loader2 size={14} className="animate-spin" /> : <Home size={14} />} Închide ziua</button>}
    </div>
  );
}
